import React, { useMemo, useState } from "react";
import { DetailValueTypeEnumValue } from "@panda0/panda0-graphql/defs";
import { Button, Checkbox, Input, Textarea } from "@panda0/panda0-ui";
import { Plus, Trash2, X } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";
import { toast } from "sonner";

import { ScreeningQuestion } from "../../types";
import { DEFAULT_QUESTION_TEXT, FormData } from "../types";
import InputTypeButtons from "./input-button";

const QualifyingQuestionsEditor: React.FC = () => {
  const resetQuestion = useMemo(() => {
    return {
      SCREENING_TEXT: "",
      SCREENING_ANSWERTYPE: DetailValueTypeEnumValue.oneline,
      SCREENING_ISREQUIRED: false,
      SCREENING_OPTIONS: ["", ""],
    };
  }, []);
  const { watch, setValue, control } = useFormContext<FormData>();
  const questions = watch("screeningQuestions") || [];

  const [currentQuestion, setCurrentQuestion] =
    useState<ScreeningQuestion>(resetQuestion);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Handle answer type change
  const handleAnswerTypeChange = (value: DetailValueTypeEnumValue) => {
    // Set default options for Radio and Checkbox questions
    let options = currentQuestion.SCREENING_OPTIONS;
    if (
      (value === DetailValueTypeEnumValue.radiobutton ||
        value === DetailValueTypeEnumValue.checkbox) &&
      (!options || options.length < 2)
    ) {
      options = ["", ""];
    }

    // Set default question text for special types
    let text = currentQuestion.SCREENING_TEXT;
    if (["Linkedin", "Github", "URL"].includes(value) && !text) {
      text =
        DEFAULT_QUESTION_TEXT[value as keyof typeof DEFAULT_QUESTION_TEXT] ||
        "";
    }

    setCurrentQuestion({
      ...currentQuestion,
      SCREENING_ANSWERTYPE: value,
      SCREENING_OPTIONS: options,
      SCREENING_TEXT: text,
    });
  };

  // Add an empty option
  const handleAddOption = () => {
    setCurrentQuestion({
      ...currentQuestion,
      SCREENING_OPTIONS: [...(currentQuestion.SCREENING_OPTIONS || []), ""],
    });
  };

  // Remove an option
  const handleRemoveOption = (index: number) => {
    if ((currentQuestion.SCREENING_OPTIONS || []).length <= 2) {
      toast.error("At least two options are required");
      return;
    }

    setCurrentQuestion({
      ...currentQuestion,
      SCREENING_OPTIONS: (currentQuestion.SCREENING_OPTIONS || []).filter(
        (_, i) => i !== index
      ),
    });
  };

  // Validate the question before adding/updating
  const validateQuestion = (): boolean => {
    // Check if question text is provided
    if (!currentQuestion.SCREENING_TEXT) {
      toast.error("Please enter a question text");
      return false;
    }

    // For multi-select questions, ensure at least two valid options
    if (
      currentQuestion.SCREENING_ANSWERTYPE ===
        DetailValueTypeEnumValue.checkbox ||
      currentQuestion.SCREENING_ANSWERTYPE ===
        DetailValueTypeEnumValue.radiobutton
    ) {
      const validOptions = (currentQuestion.SCREENING_OPTIONS || []).filter(
        (opt) => opt.trim()
      );
      if (validOptions.length < 2) {
        toast.error("Please add at least two non-empty options");
        return false;
      }
    }

    return true;
  };

  // Add a new question
  const handleAddQuestion = () => {
    if (!validateQuestion()) return;

    // Clean up the question before adding
    const questionToAdd = { ...currentQuestion };

    // Only include options for question types that need them
    if (
      questionToAdd.SCREENING_ANSWERTYPE !==
        DetailValueTypeEnumValue.checkbox &&
      questionToAdd.SCREENING_ANSWERTYPE !==
        DetailValueTypeEnumValue.radiobutton
    ) {
      questionToAdd.SCREENING_OPTIONS = [];
    } else {
      // Filter out empty options
      questionToAdd.SCREENING_OPTIONS =
        questionToAdd.SCREENING_OPTIONS?.filter((opt) => opt.trim()) || [];
    }

    // Update form state
    const updatedQuestions = [...questions, questionToAdd];
    setValue("screeningQuestions", updatedQuestions, {
      shouldValidate: true,
      shouldDirty: true,
    });

    // Reset the current question form
    setCurrentQuestion(resetQuestion);

    toast.success("Question added successfully");
  };

  // Update an existing question
  const handleUpdateQuestion = () => {
    if (!validateQuestion() || editingIndex === null) return;

    // Clean up the question before updating
    const questionToUpdate = { ...currentQuestion };

    // Only include options for question types that need them
    if (
      questionToUpdate.SCREENING_ANSWERTYPE !==
        DetailValueTypeEnumValue.checkbox &&
      questionToUpdate.SCREENING_ANSWERTYPE !==
        DetailValueTypeEnumValue.radiobutton
    ) {
      questionToUpdate.SCREENING_OPTIONS = [];
    } else {
      // Filter out empty options
      questionToUpdate.SCREENING_OPTIONS =
        questionToUpdate.SCREENING_OPTIONS?.filter((opt) => opt.trim()) || [];
    }

    // Update form state
    const updatedQuestions = [...questions];
    updatedQuestions[editingIndex] = questionToUpdate;
    setValue("screeningQuestions", updatedQuestions, {
      shouldValidate: true,
      shouldDirty: true,
    });

    // Reset editing state
    setEditingIndex(null);
    setCurrentQuestion(resetQuestion);

    toast.success("Question updated successfully");
  };

  // Delete a question
  const handleDeleteQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setValue("screeningQuestions", updatedQuestions, {
      shouldValidate: true,
      shouldDirty: true,
    });
    toast.success("Question deleted");
  };

  // Start editing a question
  const handleEditQuestion = (index: number) => {
    const questionToEdit = questions[index];
    setCurrentQuestion({
      ...questionToEdit,
      // Ensure options exist for radio/checkbox questions
      SCREENING_OPTIONS: questionToEdit.SCREENING_OPTIONS || ["", ""],
    });
    setEditingIndex(index);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setCurrentQuestion(resetQuestion);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {/* Form for adding/editing questions */}
        <div className="p-4 border rounded-md bg-gray-50">
          <h3 className="font-medium mb-4">
            {editingIndex !== null ? "Edit Question" : "Add New Question"}
          </h3>

          {/* Answer Type */}
          <div className="space-y-2 mb-4">
            <label className="block text-sm font-medium">
              Type of Question
            </label>
            <InputTypeButtons
              defaultValue={currentQuestion.SCREENING_ANSWERTYPE}
              onChange={handleAnswerTypeChange}
            />
          </div>

          {/* Question Text */}
          <div className="space-y-2 mb-4">
            <label className="block text-sm font-medium">Question Text</label>
            <Textarea
              value={currentQuestion.SCREENING_TEXT}
              onChange={(e) =>
                setCurrentQuestion({
                  ...currentQuestion,
                  SCREENING_TEXT: e.target.value,
                })
              }
              placeholder="Enter your question"
            />
          </div>

          {/* Required field */}
          <div className="flex items-center space-x-2 mb-4">
            <Checkbox
              checked={currentQuestion.SCREENING_ISREQUIRED}
              onCheckedChange={(checked) =>
                setCurrentQuestion({
                  ...currentQuestion,
                  SCREENING_ISREQUIRED: checked as boolean,
                })
              }
            />
            <label className="text-sm font-medium">Required</label>
          </div>

          {/* Options for Radio Buttons and Checkboxes */}
          {(currentQuestion.SCREENING_ANSWERTYPE ===
            DetailValueTypeEnumValue.radiobutton ||
            currentQuestion.SCREENING_ANSWERTYPE ===
              DetailValueTypeEnumValue.checkbox) && (
            <div className="space-y-2 mb-4">
              <label className="block text-sm font-medium">Options</label>
              {(currentQuestion.SCREENING_OPTIONS || []).map(
                (option, index) => (
                  <div key={index} className="flex space-x-2">
                    <Input
                      value={option}
                      onChange={(e) => {
                        const newOptions = [
                          ...(currentQuestion.SCREENING_OPTIONS || []),
                        ];
                        newOptions[index] = e.target.value;
                        setCurrentQuestion({
                          ...currentQuestion,
                          SCREENING_OPTIONS: newOptions,
                        });
                      }}
                      placeholder={`Option ${index + 1}`}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveOption(index)}
                      disabled={
                        (currentQuestion.SCREENING_OPTIONS || []).length <= 2
                      }
                      icon={<X className="h-4 w-4" />}
                    ></Button>
                  </div>
                )
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddOption}
                text="Add Option"
                icon={<Plus className="h-4 w-4 mr-1" />}
              />
            </div>
          )}

          {/* Action buttons */}
          <div className="flex space-x-2 mt-4">
            {editingIndex !== null ? (
              <>
                <Button onClick={handleUpdateQuestion} text="Update Question" />
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  text="Cancel"
                />
              </>
            ) : (
              <Button
                onClick={handleAddQuestion}
                text="Add Question"
                icon={<Plus className="h-4 w-4 mr-1" />}
              />
            )}
          </div>
        </div>

        {/* List of existing questions */}
        <div className="space-y-4">
          <h3 className="font-medium">Your Questions ({questions.length})</h3>

          {questions.length === 0 ? (
            <p className="text-gray-500 text-sm">No questions added yet.</p>
          ) : (
            <div className="space-y-2">
              {questions.map((question, index) => (
                <div
                  key={index}
                  className="p-3 border rounded-md flex justify-between items-center hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium">
                      {question.SCREENING_TEXT}
                      {question.SCREENING_ISREQUIRED && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">
                      Type: {question.SCREENING_ANSWERTYPE}
                    </p>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditQuestion(index)}
                      text="Edit"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteQuestion(index)}
                      icon={<Trash2 className="h-4 w-4 text-red-500" />}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(QualifyingQuestionsEditor);
